/**
 * SalesInvoiceImportUpload — Import PDF facture de vente historique.
 *
 * Flow en 2 étapes strictes (design volontaire, pas d'auto-save : l'humain
 * relit les champs extraits par Gemini avant toute persistance) :
 *
 *   Étape 1 — upload du PDF vers /invoices/import/extract
 *   Étape 2 — écran de revue :
 *     • Client : ClientCombobox pré-rempli avec le match trouvé (ou "+ Créer"
 *                si aucun match n'a été trouvé par SIRET/SIREN/TVA/nom+CP)
 *     • Lignes + totaux éditables (tableau)
 *     • Date d'émission, date d'échéance, notes
 *     • "Créer le brouillon" → POST /invoices (endpoint standard,
 *       find_or_create_client garantit qu'aucun doublon client n'apparaît)
 *
 * Sortie : navigation vers /app/invoices/{id}/edit (SalesDocumentEditor) où
 * l'utilisateur peut encore ajuster puis Émettre normalement.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Upload, FileText, ArrowLeft, Save, AlertTriangle, CheckCircle2, Loader2,
  Plus, Trash2, ShieldAlert, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ClientCombobox from "@/components/ClientCombobox";
import ClientEditorSheet from "@/components/ClientEditorSheet";

const VAT_RATES = [20, 10, 5.5, 2.1, 0];

const fmtEur = (n) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} €`;

export default function SalesInvoiceImportUpload() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const [fileAssetId, setFileAssetId] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [validation, setValidation] = useState(null);
  const [suggestedClient, setSuggestedClient] = useState(null); // dict from find_client
  const [selectedClient, setSelectedClient] = useState(null);    // real client_id + name after user picks
  const [duplicates, setDuplicates] = useState([]);              // factures existantes matchant le PDF
  const [invoiceNumber, setInvoiceNumber] = useState("");        // numéro extrait du PDF (préservé)

  const [form, setForm] = useState({
    invoice_date: "",
    payment_terms_days: 30,
    notes: "",
    lines: [],
  });
  // Client blob pre-filled from OCR — only used when the user opts to CREATE
  // the client (no existing match) — pushed to ClientEditorSheet `initial`.
  const [prefilledClient, setPrefilledClient] = useState(null);

  const onFilePicked = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (10 Mo max)."); return; }
    setUploading(true);
    setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/invoices/import/extract", fd,
                                { headers: { "Content-Type": "multipart/form-data" } });
      const ext = r.data.extracted || {};
      setFileAssetId(r.data.file_asset_id);
      setValidation(r.data.validation);
      setSuggestedClient(r.data.suggested_client);
      setDuplicates(r.data.duplicates || []);
      setInvoiceNumber(ext.invoice_number || "");
      if (r.data.suggested_client) {
        setSelectedClient({ ...r.data.suggested_client });
      } else {
        setPrefilledClient({
          name: ext.client_name || "",
          email: ext.client_email || "",
          address: ext.client_address || "",
          postal_code: ext.client_postal_code || "",
          city: ext.client_city || "",
          country: ext.client_country || "France",
          siret: ext.client_siret || "",
          siren: ext.client_siren || "",
          vat_number: ext.client_vat_number || "",
        });
      }
      // Prefill lines & dates
      const today = new Date().toISOString().slice(0, 10);
      const lines = (ext.lines || []).length
        ? ext.lines.map((ln) => ({
            description: ln.description || "",
            quantity: Number(ln.quantity) || 1,
            unit_price_ht: Number(ln.unit_price_ht) || 0,
            vat_rate: Number(ln.vat_rate) || 20,
            discount_percent: 0,
          }))
        : [{ description: `Facture ${ext.invoice_number || fileName}`,
              quantity: 1, unit_price_ht: Number(ext.total_ht) || 0,
              vat_rate: 20, discount_percent: 0 }];
      setForm({
        invoice_date: ext.invoice_date || today,
        payment_terms_days: 30,
        notes: ext.payment_terms
          ? `Conditions extraites du PDF : ${ext.payment_terms}` : "",
        lines,
      });
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const updLine = (i, k, v) =>
    setForm((f) => ({ ...f, lines: f.lines.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln) }));
  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, { description: "", quantity: 1,
      unit_price_ht: 0, vat_rate: 20, discount_percent: 0 }] }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const totals = form.lines.reduce((acc, ln) => {
    const net = Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0);
    const vat = net * Number(ln.vat_rate || 0) / 100;
    acc.ht += net; acc.vat += vat; acc.ttc += net + vat;
    return acc;
  }, { ht: 0, vat: 0, ttc: 0 });

  const save = async (force = false) => {
    if (!selectedClient) { toast.error("Sélectionne ou crée le client d'abord."); return; }
    if (!invoiceNumber?.trim()) {
      toast.error("Numéro de facture d'origine manquant — renseigne-le à la main.");
      return;
    }
    for (let i = 0; i < form.lines.length; i++) {
      const ln = form.lines[i];
      if (!ln.description?.trim()) { toast.error(`Ligne ${i+1} : désignation obligatoire.`); return; }
    }
    setSaving(true);
    try {
      // Import archive : la facture est créée DIRECTEMENT en "émise" avec le
      // numéro d'origine du PDF (pas de re-numérotation, pas de brouillon).
      const payload = {
        invoice_number: invoiceNumber.trim(),
        invoice_date: form.invoice_date,
        client: {
          name: selectedClient.name,
          email: selectedClient.email || null,
          address: selectedClient.address || null,
          postal_code: selectedClient.postal_code || null,
          city: selectedClient.city || null,
          country: selectedClient.country || "France",
          siren: selectedClient.siren || null,
          siret: selectedClient.siret || null,
          vat_number: selectedClient.vat_number || null,
        },
        lines: form.lines.map((ln) => ({
          description: ln.description,
          quantity: Number(ln.quantity),
          unit_price_ht: Number(ln.unit_price_ht),
          vat_rate: Number(ln.vat_rate),
          discount_percent: Number(ln.discount_percent) || 0,
        })),
        notes: form.notes || null,
        payment_terms_days: Number(form.payment_terms_days) || 30,
        file_asset_id: fileAssetId || null,
        force,
      };
      const { data } = await api.post("/invoices/import/save", payload);
      const invId = data.invoice.id;
      toast.success(`Facture ${data.invoice.number} importée dans l'archive`);
      navigate(`/app/invoices/${invId}`);
    } catch (e) {
      // 409 = doublon détecté côté serveur → propose de forcer
      const status = e?.response?.status;
      if (status === 409 && !force) {
        toast.error("Doublon détecté — utilise le bouton \"Importer quand même\" si c'est volontaire.");
      } else {
        toast.error(apiError(e));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="sales-import-page">
      <div className="flex items-center gap-3">
        <Link to="/app/invoices">
          <Button variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="h-4 w-4 mr-1" /> Factures émises
          </Button>
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Archiver une facture externe (PDF)
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Enregistre dans Averdy Agency une facture <b>déjà émise ailleurs</b> (papier, autre logiciel, envoyée manuellement…).
          L'IA extrait le client + les lignes, tu confirmes et la facture est classée dans l'archive comptable avec
          son <b>numéro d'origine préservé</b> — aucune renumérotation, aucun envoi email.
        </p>
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span>Si tu veux <b>créer une nouvelle facture</b> (émission depuis Averdy Agency, envoi email au client, numérotation légale), retourne dans <Link to="/app/invoices" className="text-blue-700 hover:underline font-medium">Factures émises</Link> puis "Nouvelle facture".</span>
        </div>
      </div>

      {step === 1 && (
        <Card className="border-slate-200 bg-white rounded-xl">
          <label className={cn("block cursor-pointer p-12 text-center border-2 border-dashed rounded-xl",
            "border-slate-200 hover:border-blue-400 hover:bg-blue-50/30 transition-colors")}
                  data-testid="import-upload-zone">
            <input id="import-file-input" name="import-file-input"
                    type="file" accept="application/pdf,image/*" className="hidden"
                    disabled={uploading}
                    onChange={(e) => onFilePicked(e.target.files?.[0])}
                    data-testid="import-file-input" />
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 mx-auto text-blue-600 animate-spin mb-3" />
                <p className="text-sm text-slate-700">Extraction en cours…</p>
                <p className="text-xs text-slate-400 mt-1">{fileName}</p>
              </>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  Glisse ton PDF ici ou clique pour en choisir un
                </p>
                <p className="text-xs text-slate-400 mt-1">PDF ou image, 10 Mo max</p>
              </>
            )}
          </label>
        </Card>
      )}

      {step === 2 && (
        <>
          {duplicates.length > 0 && (
            <Alert className="border-red-300 bg-red-50" data-testid="import-duplicates-alert">
              <ShieldAlert className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-900">
                <p className="font-semibold mb-1">
                  ⚠ {duplicates.length} facture{duplicates.length > 1 ? "s" : ""} déjà présente{duplicates.length > 1 ? "s" : ""} avec ce numéro / ce montant :
                </p>
                <ul className="space-y-1 mt-2">
                  {duplicates.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 text-xs">
                      <Link to={`/app/invoices/${d.id}`} className="text-red-800 hover:underline flex items-center gap-1"
                             data-testid={`dup-link-${d.id}`}>
                        <ExternalLink className="h-3 w-3" />
                        <b>{d.number || "sans n°"}</b>
                      </Link>
                      <span className="text-red-700">
                        · {d.client_name || "—"} · {fmtEur(d.total_ttc || 0)} · {d.invoice_date?.slice(0, 10) || "—"}
                      </span>
                      <span className="ml-auto uppercase text-[10px] tracking-wide bg-red-200 text-red-900 px-1.5 py-0.5 rounded">
                        {d.source === "axonaut" ? "Axonaut" :
                         d.source === "pennylane" ? "Pennylane" :
                         d.source === "native" ? "Créée sur Averdy Agency" :
                         d.source === "pdf_import" ? "Import PDF précédent" :
                         d.source === "connector" ? "Connecteur" :
                         d.source}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs">
                  Si tu importes volontairement un doublon (ex : réémission), utilise <b>« Importer quand même »</b>.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {validation && !validation.ok && (
            <Alert className="border-amber-200 bg-amber-50" data-testid="import-validation-alert">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800">
                Vérifie les valeurs extraites — l'IA a signalé :
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {(validation.issues || []).map((iss, i) => (
                    <li key={i}>{iss.message || iss.code}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-slate-200 bg-white rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FileText className="h-3.5 w-3.5 text-blue-600" />
              <span className="truncate">{fileName}</span>
              {fileAssetId && (
                <a href={`${API}/files/${fileAssetId}`} target="_blank" rel="noreferrer"
                    className="text-blue-600 hover:underline ml-auto"
                    data-testid="import-view-pdf">
                  Voir le PDF
                </a>
              )}
            </div>

            {/* --- Numéro de facture d'origine (préservé) --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-3 border-b border-slate-100">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Numéro d'origine (préservé) *
                </Label>
                <Input value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        placeholder="Ex: FC-2024-00123"
                        className="mt-1 bg-white border-slate-200 font-mono"
                        data-testid="import-invoice-number" />
                <p className="text-[10px] text-slate-400 mt-1">
                  Le numéro extrait du PDF est conservé tel quel. Pas de re-numérotation.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Date d'émission d'origine *
                </Label>
                <div className="mt-1">
                  <DatePickerFR value={form.invoice_date}
                                 onChange={(v) => setForm({ ...form, invoice_date: v })} />
                </div>
              </div>
            </div>

            {/* --- Client --- */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Client</Label>
              {suggestedClient && selectedClient?.id === suggestedClient.id && (
                <div className="mb-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                      data-testid="import-client-match-badge">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Client existant reconnu depuis le PDF
                </div>
              )}
              {!suggestedClient && prefilledClient && (
                <div className="mb-2 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  Aucun client existant — crée-le à partir des infos extraites du PDF.
                  <Button variant="link" size="sm" className="ml-auto h-6 px-1 text-blue-700"
                           onClick={() => setCreatorOpen(true)}
                           data-testid="import-create-client-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Créer « {prefilledClient.name} »
                  </Button>
                </div>
              )}
              <ClientCombobox
                value={selectedClient}
                onSelect={(c) => setSelectedClient(c)}
                onClear={() => setSelectedClient(null)}
              />
            </div>

            {/* --- Lignes --- */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Lignes extraites ({form.lines.length})
                </Label>
                <Button variant="ghost" size="sm" onClick={addLine}
                         className="h-7 text-blue-700 hover:bg-blue-50">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Désignation</th>
                      <th className="text-right px-2 py-1.5 w-16">Qté</th>
                      <th className="text-right px-2 py-1.5 w-24">PU HT</th>
                      <th className="text-right px-2 py-1.5 w-16">TVA</th>
                      <th className="text-right px-2 py-1.5 w-24">HT</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => {
                      const net = Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0);
                      return (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1">
                            <Input value={ln.description}
                                    onChange={(e) => updLine(i, "description", e.target.value)}
                                    className="h-8 bg-white border-slate-200"
                                    data-testid={`import-line-desc-${i}`} />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" min="0" step="0.01" value={ln.quantity}
                                    onChange={(e) => updLine(i, "quantity", e.target.value)}
                                    className="h-8 text-right font-mono bg-white border-slate-200" />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" min="0" step="0.01" value={ln.unit_price_ht}
                                    onChange={(e) => updLine(i, "unit_price_ht", e.target.value)}
                                    className="h-8 text-right font-mono bg-white border-slate-200" />
                          </td>
                          <td className="px-2 py-1">
                            <select id={`import-line-vat-${i}`} name={`import-line-vat-${i}`}
                                     value={ln.vat_rate}
                                     onChange={(e) => updLine(i, "vat_rate", Number(e.target.value))}
                                     className="h-8 w-full font-mono text-right bg-white border border-slate-200 rounded px-1">
                              {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1 text-right font-mono tabular-nums">{fmtEur(net)}</td>
                          <td className="px-1 text-center">
                            {form.lines.length > 1 && (
                              <button onClick={() => removeLine(i)}
                                       className="text-slate-400 hover:text-red-600 p-1"
                                       aria-label="Supprimer la ligne">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex justify-end gap-6 text-sm">
                <span className="text-slate-600">HT <b className="font-mono ml-1">{fmtEur(totals.ht)}</b></span>
                <span className="text-slate-600">TVA <b className="font-mono ml-1">{fmtEur(totals.vat)}</b></span>
                <span className="text-slate-900">TTC <b className="font-mono ml-1"
                                                          data-testid="import-total-ttc">{fmtEur(totals.ttc)}</b></span>
              </div>
            </div>

            {/* --- Notes + délai --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Délai de paiement (jours)</Label>
                <Input type="number" min="0" value={form.payment_terms_days}
                        onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })}
                        className="mt-1 bg-white border-slate-200 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea value={form.notes}
                         onChange={(e) => setForm({ ...form, notes: e.target.value })}
                         className="mt-1 bg-white border-slate-200 min-h-[70px]" />
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Reprendre un autre PDF
              </Button>
              <div className="flex gap-2">
                {duplicates.length > 0 && (
                  <Button variant="outline" onClick={() => save(true)} disabled={saving || !selectedClient}
                           className="border-red-300 text-red-700 hover:bg-red-50"
                           data-testid="import-force-btn">
                    <ShieldAlert className="h-4 w-4 mr-1.5" />
                    Importer quand même
                  </Button>
                )}
                <Button onClick={() => save(false)}
                         disabled={saving || !selectedClient || duplicates.length > 0}
                         className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-300"
                         data-testid="import-save-btn">
                  <Save className="h-4 w-4 mr-1.5" />
                  {saving ? "Import…" : "Enregistrer la facture importée"}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Modal création client avec les infos pré-remplies du PDF */}
      <ClientEditorSheet
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        initial={prefilledClient}
        onSaved={(c) => { setSelectedClient(c); setCreatorOpen(false); }}
      />
    </div>
  );
}
